/**
 * Gerador de PDF profissional para Relatório de Projetos AgroLink
 *
 * Estrutura:
 * 1. Faixa verde de topo com logo + nome empresa + título
 * 2. Info box: emitido em, emitido por, total de projetos
 * 3. Card de filtros aplicados
 * 4. Grid de totalizadores (Projetos, Valor Total, Com Pendência, Em Andamento, Arquivados, Ticket Médio)
 * 5. Distribuição por Etapa + Distribuição por Tipo (2 colunas)
 * 6. Tabela principal: Projetos
 * 7. Detalhado=true → linha adicional por projeto com: histórico de etapas, dias por etapa
 * 8. Rodapé com paginação
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtMoney = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtCPF = (c) => c ? String(c).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR') : '—';

// Paleta verde (matching primary buttons)
const COR_PRIMARIA = [21, 128, 61];
const COR_ESCURA = [15, 23, 42];
const COR_MEDIA = [71, 85, 105];
const COR_CLARA = [148, 163, 184];
const COR_BORDA = [226, 232, 240];
const COR_HIGHLIGHT = [240, 253, 244];
const COR_VERDE = [16, 185, 129];
const COR_VERMELHO = [239, 68, 68];
const COR_AMBAR = [245, 158, 11];
const COR_AZUL = [59, 130, 246];

export async function gerarRelatorioProjetosPDF({ report, detalhado, empresa, filtrosLabels, geradoPor }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  const nomeEmpresa = empresa?.nome_empresa || 'AgroLink CRM';
  const logoBase64 = empresa?.logo_base64;
  const geradoEm = new Date().toISOString();

  // ============ CABEÇALHO ============
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, pageWidth, 30, 'F');

  let logoOffset = margin;
  if (logoBase64) {
    try {
      const src = logoBase64.startsWith('data:') ? logoBase64 : `data:image/png;base64,${logoBase64}`;
      doc.addImage(src, 'PNG', margin, 6, 18, 18);
      logoOffset = margin + 22;
    } catch (e) {
      console.warn('Falha ao adicionar logo:', e);
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(nomeEmpresa.toUpperCase(), logoOffset, 14);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(220, 252, 231);
  doc.text('Sistema de Gestão AgroLink', logoOffset, 20);

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('RELATÓRIO DE PROJETOS', pageWidth - margin, 14, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Crédito Rural · Pipeline · Pendências', pageWidth - margin, 20, { align: 'right' });

  let cursorY = 38;

  // ============ INFO BOX ============
  const infoBoxY = cursorY;
  doc.setFillColor(...COR_HIGHLIGHT);
  doc.setDrawColor(...COR_BORDA);
  doc.roundedRect(margin, infoBoxY, pageWidth - margin * 2, 14, 1.5, 1.5, 'FD');

  const colWidth = (pageWidth - margin * 2) / 3;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('EMITIDO EM', margin + 4, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(fmtDateTime(geradoEm), margin + 4, infoBoxY + 10);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('EMITIDO POR', margin + 4 + colWidth, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(geradoPor || '—', margin + 4 + colWidth, infoBoxY + 10);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('PROJETOS NO RELATÓRIO', margin + 4 + colWidth * 2, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(`${report.resumo.total_projetos} projeto(s) · ${fmtMoney(report.resumo.total_credito)}`, margin + 4 + colWidth * 2, infoBoxY + 10);

  cursorY = infoBoxY + 18;

  // ============ FILTROS ============
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...COR_BORDA);
  const filtrosH = Math.max(14, 6 + Math.ceil(filtrosLabels.length / 3) * 5);
  doc.roundedRect(margin, cursorY, pageWidth - margin * 2, filtrosH, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COR_PRIMARIA);
  doc.text('FILTROS APLICADOS', margin + 4, cursorY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  filtrosLabels.forEach((f, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = margin + 4 + col * ((pageWidth - margin * 2) / 3);
    const y = cursorY + 10 + row * 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COR_MEDIA);
    doc.text(`${f.label}:`, x, y);
    const labelWidth = doc.getTextWidth(`${f.label}: `);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COR_ESCURA);
    const truncated = String(f.value).length > 45 ? String(f.value).substring(0, 45) + '...' : String(f.value);
    doc.text(truncated, x + labelWidth, y);
  });

  cursorY += filtrosH + 4;

  // ============ TOTALIZADORES ============
  const ticketMedio = report.resumo.total_projetos > 0
    ? report.resumo.total_credito / report.resumo.total_projetos
    : 0;

  const totais = [
    { label: 'PROJETOS', value: String(report.resumo.total_projetos), hint: fmtMoney(report.resumo.total_credito), cor: COR_PRIMARIA },
    { label: 'EM ANDAMENTO', value: String(report.resumo.por_status.em_andamento || 0), hint: null, cor: COR_AZUL },
    { label: 'ARQUIVADOS', value: String(report.resumo.por_status.arquivado || 0), hint: null, cor: COR_VERDE },
    { label: 'DESISTIDOS', value: String(report.resumo.por_status.desistido || 0), hint: null, cor: COR_CLARA },
    { label: 'PENDÊNCIAS', value: String(report.resumo.com_pendencia || 0), hint: null, cor: COR_AMBAR },
    { label: 'TICKET MÉDIO', value: fmtMoney(ticketMedio), hint: null, cor: COR_PRIMARIA },
  ];

  const cardW = (pageWidth - margin * 2 - 5 * 2) / 6;
  const cardH = 18;
  totais.forEach((t, i) => {
    const x = margin + i * (cardW + 2);
    doc.setFillColor(252, 252, 251);
    doc.setDrawColor(...t.cor);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, cursorY, cardW, cardH, 1.5, 1.5, 'FD');
    doc.setFillColor(...t.cor);
    doc.rect(x, cursorY, 1.5, cardH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...t.cor);
    doc.text(t.label, x + 4, cursorY + 5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(t.value.length > 10 ? 9 : 11);
    doc.setTextColor(...COR_ESCURA);
    doc.text(t.value, x + 4, cursorY + 11);

    if (t.hint) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...COR_CLARA);
      doc.text(t.hint, x + 4, cursorY + 15);
    }
  });

  cursorY += cardH + 6;

  // ============ DISTRIBUIÇÃO POR ETAPA + TIPO (2 colunas) ============
  const porEtapa = Object.entries(report.resumo.por_etapa || {});
  const porTipo = Object.entries(report.resumo.por_tipo_projeto || {});

  if (porEtapa.length > 0 || porTipo.length > 0) {
    const colW = (pageWidth - margin * 2 - 4) / 2;
    let finalY1 = cursorY, finalY2 = cursorY;

    if (porEtapa.length > 0) {
      autoTable(doc, {
        startY: cursorY,
        head: [['Etapa', 'Qtde']],
        body: porEtapa.map(([nome, qtde]) => [nome, String(qtde)]),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.5, lineColor: COR_BORDA, lineWidth: 0.1 },
        headStyles: { fillColor: COR_PRIMARIA, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
          0: { cellWidth: colW - 20 },
          1: { halign: 'right', cellWidth: 20, fontStyle: 'bold' },
        },
        margin: { left: margin, right: pageWidth - margin - colW },
      });
      finalY1 = doc.lastAutoTable.finalY;
    }

    if (porTipo.length > 0) {
      autoTable(doc, {
        startY: cursorY,
        head: [['Tipo de Projeto', 'Qtde']],
        body: porTipo.map(([nome, qtde]) => [nome, String(qtde)]),
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 1.5, lineColor: COR_BORDA, lineWidth: 0.1 },
        headStyles: { fillColor: COR_PRIMARIA, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8 },
        columnStyles: {
          0: { cellWidth: colW - 20 },
          1: { halign: 'right', cellWidth: 20, fontStyle: 'bold' },
        },
        margin: { left: margin + colW + 4, right: margin },
      });
      finalY2 = doc.lastAutoTable.finalY;
    }

    cursorY = Math.max(finalY1, finalY2) + 6;
  }

  // ============ TABELA PRINCIPAL: PROJETOS ============
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COR_PRIMARIA);
  doc.text(`PROJETOS (${report.projetos.length})`, margin, cursorY);
  cursorY += 2;

  const projBody = [];
  report.projetos.forEach((p) => {
    projBody.push([
      p.cliente_nome || '—',
      fmtCPF(p.cliente_cpf),
      p.tipo_projeto || '—',
      p.instituicao || '—',
      fmtMoney(p.valor_credito),
      p.etapa_atual || '—',
      p.status || '—',
      `${p.duracao_total_dias || 0} d`,
      p.parceiro || '—',
      p.usuario || '—',
      p.tem_pendencia ? 'SIM' : 'não',
    ]);

    // Linha detalhada se solicitado
    if (detalhado) {
      const detalhes = [];
      if (p.data_inicio) detalhes.push(`Início: ${fmtDate(p.data_inicio)}`);
      if (p.data_arquivamento) detalhes.push(`Arquivado: ${fmtDate(p.data_arquivamento)}`);
      if (p.motivo_desistencia) detalhes.push(`Desistência: ${p.motivo_desistencia}`);
      if (p.observacoes) detalhes.push(`Obs: ${p.observacoes}`);

      // Histórico de etapas se houver
      if (p.historico_etapas && p.historico_etapas.length > 0) {
        const historico = p.historico_etapas
          .map(h => `» ${h.etapa_nome || '?'} (${h.dias || 0}d)`)
          .join('  ');
        detalhes.push(`Etapas: ${historico}`);
      }

      if (detalhes.length > 0) {
        projBody.push([{
          content: detalhes.join('\n'),
          colSpan: 11,
          styles: {
            fillColor: [248, 250, 252],
            textColor: COR_MEDIA,
            fontSize: 7,
            cellPadding: { top: 2, right: 4, bottom: 2, left: 8 },
          }
        }]);
      }
    }
  });

  // Linha total
  projBody.push([
    { content: 'TOTAIS', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold', fillColor: COR_HIGHLIGHT, textColor: COR_ESCURA } },
    { content: fmtMoney(report.resumo.total_credito), styles: { halign: 'right', fontStyle: 'bold', fillColor: COR_HIGHLIGHT } },
    { content: '', colSpan: 5, styles: { fillColor: COR_HIGHLIGHT } },
    { content: `${report.resumo.com_pendencia} pend.`, styles: { halign: 'center', fontStyle: 'bold', fillColor: COR_HIGHLIGHT, textColor: [153, 27, 27] } },
  ]);

  autoTable(doc, {
    startY: cursorY,
    head: [['Cliente', 'CPF', 'Tipo', 'Instituição', 'Valor', 'Etapa', 'Status', 'Dur.', 'Parceiro', 'Usuário', 'Pend.']],
    body: projBody,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.8, lineColor: COR_BORDA, lineWidth: 0.1, overflow: 'linebreak' },
    headStyles: { fillColor: COR_PRIMARIA, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, halign: 'left' },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 24 },
      2: { cellWidth: 22 },
      3: { cellWidth: 30 },
      4: { halign: 'right', cellWidth: 26, fontStyle: 'bold' },
      5: { cellWidth: 26 },
      6: { cellWidth: 20 },
      7: { halign: 'center', cellWidth: 12 },
      8: { cellWidth: 22 },
      9: { cellWidth: 22 },
      10: { halign: 'center', cellWidth: 12 },
    },
    margin: { left: margin, right: margin },
    didParseCell: (cell) => {
      if (cell.section === 'body' && cell.column.index === 10 && cell.cell.raw === 'SIM') {
        cell.cell.styles.textColor = [153, 27, 27];
        cell.cell.styles.fontStyle = 'bold';
      }
    },
    didDrawPage: () => {
      const p = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(7);
      doc.setTextColor(...COR_CLARA);
      doc.setFont('helvetica', 'normal');
      doc.text(`${nomeEmpresa} · Sistema AgroLink CRM`, margin, pageHeight - 5);
      doc.text(`Página ${p} de {total_pages_count_string}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
      doc.text(`Gerado em ${fmtDateTime(geradoEm)}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    },
  });

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages('{total_pages_count_string}');
  }

  const dataStr = new Date().toISOString().slice(0, 10);
  const nomeArq = `Relatorio_Projetos_${nomeEmpresa.replace(/[^a-z0-9]/gi, '_')}_${dataStr}.pdf`;
  doc.save(nomeArq);
}
